import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../provider/AuthProvider";
import { loginWithPassword, requestCode } from "../services/authService";
import { validateEmail } from "../utils/emailUtil";

export default function LoginPage() {
  const [loginMethod, setLoginMethod] = useState("login"); // "email" or "login"
  const [email, setEmail] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [inputError, setInputError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();

  // Handle email login submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateEmail(email)) {
      setInputError("Zadejte platnou e-mailovou adresu.");
      return;
    }
    setInputError("");
    setIsSubmitting(true);
    try {
      const result = await requestCode(email, "login");
      if (result.success) {
        navigate("/verify-code", { state: { mode: "login", email } });
      } else {
        if (result.status === 404) {
          setInputError(result.message);
        } else {
          const customError = new Error(result.message || "Neznámá chyba");
          customError.status = result.status;
          setError(customError);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!login || !password) {
      setInputError("Vyplňte prosím odběratelské číslo a heslo.");
      return;
    }
    setInputError("");
    setIsSubmitting(true);
    try {
      const result = await loginWithPassword(login, password);
      if (result.success) {
        if (result.mfaRequired) {
          navigate("/verify-code", {
            state: { mode: "password-mfa", mfaTyp: result.mfaTyp },
          });
          return;
        }
        setAccessToken(result.token);
        navigate("/zvk");
      } else {
        if (result.status === 404 || result.status === 400) {
          setInputError(result.message);
        } else {
          const customError = new Error(result.message || "Neznámá chyba");
          customError.status = result.status;
          setError(customError);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    throw new Response(error.message, { status: error.status });
  }

  return (
    <Box style={{ maxWidth: "400px", margin: "10px auto" }}>
      <Typography
        variant="h6"
        sx={{
          fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.7rem" }, // responsive font size
          mt: 4,
          mb: 2,
        }}
      >
        Přihlášení
      </Typography>
      <ButtonGroup
        variant="contained"
        fullWidth
        style={{ marginBottom: "20px" }}
      >
        <Button
          disabled={isSubmitting}
          onClick={() => {
            setLoginMethod("login");
            setInputError("");
          }}
          color={loginMethod === "login" ? "primary" : "white"}
        >
          Přihlásit se číslem odběratele
        </Button>
        <Button
          disabled={isSubmitting}
          onClick={() => {
            setLoginMethod("email");
            setInputError("");
          }}
          color={loginMethod === "email" ? "primary" : "white"}
        >
          Přihlásit se emailem
        </Button>
      </ButtonGroup>

      {loginMethod === "email" ? (
        <Box component="form" onSubmit={handleEmailSubmit}>
          {inputError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inputError}
            </Alert>
          )}
          <Box style={{ marginBottom: "10px" }}>
            <TextField
              id="email"
              label="Email"
              variant="outlined"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              required
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            style={{ display: "block", margin: "5px auto", width: "150px" }}
          >
            {isSubmitting ? "Odesílám…" : "Přihlásit se"}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleLoginSubmit}>
          {inputError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inputError}
            </Alert>
          )}
          <Box style={{ marginBottom: "10px" }}>
            <TextField
              id="customerNubmer"
              label="Číslo odběratele"
              variant="outlined"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              required
            />
          </Box>
          <Box style={{ marginBottom: "10px" }}>
            <TextField
              id="password"
              label="Heslo"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              required
              autoComplete="off"
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            style={{ display: "block", margin: "0px auto", width: "150px" }}
          >
            {isSubmitting ? "Přihlašuji…" : "Přihlásit se"}
          </Button>
        </Box>
      )}

      <Button
        variant="outlined"
        style={{ display: "block", margin: "5px auto", width: "150px" }}
        onClick={() => navigate("/register")}
      >
        Registrace
      </Button>
      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Link
          component="button"
          onClick={() => {
            navigate("/forgot-password");
          }}
          underline="none"
        >
          Zapomněli jste heslo?
        </Link>
      </Box>
    </Box>
  );
}
